import { Alert, Button, Card, EmptyState, PageHeader, Skeleton } from '@catering/ui';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Link, getRouteApi } from '@tanstack/react-router';
import { Plus, Search, UtensilsCrossed } from 'lucide-react';
import type { JSX } from 'react';

import { hasPermission } from '../../auth/has-permission';
import { useCurrentUser } from '../../auth/use-current-user';
import type { MenuStatusFilter } from '../../menus/api';
import { listAllergens, listDietaryTags, listServiceTypes, searchMenus } from '../../menus/api';
import { GuideLink } from '../../guide/guide-link';
import { MenuSearchBar } from './components/menu-search-bar';
import { MenuTable } from './components/menu-table';

const PAGE_SIZE = 20;

// `protectedRoute` in `router.tsx` is pathless (`id: 'protected'`), so a
// child declared with `path: '/menus'` still gets nested under it in the
// route id, even though its URL is the absolute `/menus`.
const menusRoute = getRouteApi('/protected/menus');

interface FilterPatch {
  q?: string;
  serviceTypeId?: string | undefined;
  dietaryTagId?: string | undefined;
  excludeAllergenId?: string | undefined;
  status?: MenuStatusFilter | undefined;
}

/**
 * `menu-search/spec.md` → "Pantalla de listado en back-office". List state
 * lives in the URL (`validateSearch` in `router.tsx`) so reload/back keep
 * it, per `design.md` §7.
 */
export function MenuListPage(): JSX.Element {
  const search = menusRoute.useSearch();
  const currentUser = useCurrentUser();
  const canCreate = hasPermission(currentUser.data, 'menu.create');
  const navigate = menusRoute.useNavigate();

  const serviceTypesQuery = useQuery({ queryKey: ['service-types'], queryFn: listServiceTypes });
  const dietaryTagsQuery = useQuery({ queryKey: ['dietary-tags'], queryFn: listDietaryTags });
  const allergensQuery = useQuery({ queryKey: ['allergens'], queryFn: listAllergens });

  const query = useQuery({
    queryKey: [
      'menus',
      search.q,
      search.serviceTypeId,
      search.dietaryTagId,
      search.excludeAllergenId,
      search.status,
      search.page,
    ],
    queryFn: () =>
      searchMenus({
        q: search.q === '' ? undefined : search.q,
        serviceTypeId: search.serviceTypeId,
        dietaryTagId: search.dietaryTagId,
        excludeAllergenId: search.excludeAllergenId,
        status: search.status,
        page: search.page,
        pageSize: PAGE_SIZE,
      }),
    placeholderData: keepPreviousData,
  });

  function applyFilters(patch: FilterPatch): void {
    void navigate({ search: (prev) => ({ ...prev, ...patch, page: 1 }) });
  }

  function goToPage(page: number): void {
    void navigate({ search: (prev) => ({ ...prev, page }) });
  }

  const hasActiveFilters =
    search.q !== '' ||
    search.serviceTypeId !== undefined ||
    search.dietaryTagId !== undefined ||
    search.excludeAllergenId !== undefined ||
    search.status !== undefined;
  const totalPages =
    query.data === undefined ? 1 : Math.max(1, Math.ceil(query.data.total / PAGE_SIZE));
  const archived = search.status === 'ARCHIVED';

  return (
    <section className="flex flex-col gap-6">
      <PageHeader
        icon={UtensilsCrossed}
        title="Catálogo de menús"
        description="Armá y mantené el catálogo de menús disponibles para tus clientes."
        actions={
          <div className="flex items-center gap-4">
            <Link
              to="/menus/categories"
              className="text-sm font-medium text-slate-700 hover:underline"
            >
              Categorías
            </Link>
            {canCreate ? (
              <Link to="/menus/new">
                <Button icon={Plus}>Nuevo menú</Button>
              </Link>
            ) : null}
            <GuideLink moduleId="catalog" />
          </div>
        }
      />

      <Card>
        <MenuSearchBar
          query={search.q}
          serviceTypeId={search.serviceTypeId}
          dietaryTagId={search.dietaryTagId}
          excludeAllergenId={search.excludeAllergenId}
          status={search.status}
          serviceTypes={serviceTypesQuery.data ?? []}
          dietaryTags={dietaryTagsQuery.data ?? []}
          allergens={allergensQuery.data ?? []}
          onQueryChange={(q) => applyFilters({ q })}
          onServiceTypeChange={(serviceTypeId) => applyFilters({ serviceTypeId })}
          onDietaryTagChange={(dietaryTagId) => applyFilters({ dietaryTagId })}
          onExcludeAllergenChange={(excludeAllergenId) => applyFilters({ excludeAllergenId })}
          onStatusChange={(status) => applyFilters({ status })}
        />
      </Card>

      {query.isError ? (
        <Alert tone="danger" title="No pudimos cargar los menús">
          <div className="flex items-center justify-between gap-4">
            <span>Ocurrió un error al buscar. Intentá de nuevo.</span>
            <Button variant="secondary" size="sm" onClick={() => void query.refetch()}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {query.isPending ? (
        <div className="flex flex-col gap-2">
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
          <Skeleton className="h-10 w-full" />
        </div>
      ) : null}

      {query.data !== undefined && query.data.items.length === 0 ? (
        <EmptyState
          icon={hasActiveFilters ? Search : UtensilsCrossed}
          title={
            hasActiveFilters ? 'No se encontraron menús.' : 'Todavía no hay menús registrados.'
          }
        />
      ) : null}

      {query.data !== undefined && query.data.items.length > 0 ? (
        <>
          <MenuTable
            items={query.data.items}
            serviceTypes={serviceTypesQuery.data ?? []}
            archived={archived}
          />

          <div className="flex items-center justify-between text-sm text-slate-600">
            <span>
              {query.data.total} {query.data.total === 1 ? 'menú' : 'menús'} en total
            </span>

            <div className="flex items-center gap-3">
              <Button
                variant="secondary"
                size="sm"
                disabled={search.page <= 1}
                onClick={() => goToPage(search.page - 1)}
              >
                Anterior
              </Button>
              <span>
                Página {search.page} de {totalPages}
              </span>
              <Button
                variant="secondary"
                size="sm"
                disabled={search.page >= totalPages}
                onClick={() => goToPage(search.page + 1)}
              >
                Siguiente
              </Button>
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
}
